/**
 * Lightweight client-side version polling.
 *
 * Goal: detect a fresh deploy *before* the user navigates and triggers a
 * dynamic-import failure (which would briefly white-screen on the Suspense
 * fallback). When a new build is detected, we fire `app:update-required` so
 * the AppErrorBoundary can render the bottom-corner "تحديث جديد متاح"
 * banner over the user's current page without disrupting it.
 *
 * Strategy:
 *  - Read the build id baked at compile time (__APP_BUILD_ID__).
 *  - Periodically fetch /index.html with cache:'no-store' and read the
 *    `<meta name="app-build-id">` value from the response.
 *  - If it differs from our compile-time id → notify once.
 *  - Re-check on tab refocus (visibilitychange) so users returning to a
 *    long-open tab learn about the new version immediately.
 *
 * Failures (offline, 404, parse error) are swallowed silently — this is a
 * background convenience, not a critical path.
 */

const POLL_INTERVAL_MS = 60_000;

let started = false;
let intervalId = null;
let notified = false;
let currentBuildId = null;

function getCurrentBuildId() {
  try {
    if (typeof __APP_BUILD_ID__ !== "undefined") return String(__APP_BUILD_ID__);
  } catch {
    // ignore — token not defined in this environment
  }
  return null;
}

async function fetchRemoteBuildId() {
  try {
    const res = await fetch(`/index.html?_v=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(/<meta\s+name=["']app-build-id["']\s+content=["']([^"']+)["']/i);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

function notifyOnce() {
  if (notified) return;
  notified = true;
  try {
    window.dispatchEvent(new CustomEvent("app:update-required"));
  } catch {
    // ignore
  }
}

async function checkOnce() {
  if (notified || !currentBuildId) return;
  const remote = await fetchRemoteBuildId();
  if (!remote) return;
  // Treat a placeholder (dev mode where the token isn't replaced) as a no-op.
  if (remote === "__BUILD_ID__") return;
  if (remote !== currentBuildId) {
    notifyOnce();
  }
}

export function startVersionCheck() {
  if (started || typeof window === "undefined") return;
  started = true;
  currentBuildId = getCurrentBuildId();
  // No baked id → nothing reliable to compare against. Bail out quietly.
  if (!currentBuildId || currentBuildId === "__BUILD_ID__") return;

  // Initial check after a short delay (don't compete with first paint).
  setTimeout(() => {
    checkOnce();
  }, 5_000);

  intervalId = setInterval(checkOnce, POLL_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkOnce();
    }
  });
}

export function stopVersionCheck() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  started = false;
}